import React, { useState, useEffect } from 'react';
import {
  X, Pencil, Check, Loader2, Trash2, Building2, Globe2, Phone,
  MapPin, Briefcase, Search, Plus, FolderOpen, StickyNote, AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ModalOverlay from '../../../components/ui/ModalOverlay';
import { BRAND_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { clientService } from '../api/clientsApi';
import { projectService } from '../../projects';
import {
  getInitials, getAvatarColor, fmt,
  statusCls, statusLabel, INDUSTRIES, fieldCls,
} from './clientHelpers';

// ─── DeleteConfirmModal ──────────────────────────────────────────────────────

const DeleteConfirmModal = ({ client, onConfirm, onCancel, deleting }) => (
  <ModalOverlay isOpen onClose={onCancel} closeOnOverlayClick zIndex={60}>
    <div className="rounded-lg shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-red-100 rounded-full">
          <AlertTriangle size={18} className="text-red-600" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Delete Client</p>
          <p className="text-sm text-gray-500 mt-0.5">
            This removes the client record — the linked user account is not deleted.
          </p>
        </div>
      </div>
      <div className="px-4 py-3 text-sm text-gray-700 border rounded-xl"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
        <span className="font-semibold">{client.organization || client.name}</span>
        {client.email && <span className="ml-2 text-gray-400">({client.email})</span>}
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={deleting}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
          {deleting ? <><Loader2 size={12} className="animate-spin" /> Deleting…</> : <><Trash2 size={13} /> Delete</>}
        </button>
      </div>
    </div>
  </ModalOverlay>
);

// ─── NotesTab ────────────────────────────────────────────────────────────────

const NotesTab = ({ client, canManage, onUpdated }) => {
  const [notes, setNotes] = useState(client.notes || '');
  const [saving, setSaving] = useState(false);
  const isDirty = notes !== (client.notes || '');

  const save = async () => {
    setSaving(true);
    try {
      const updated = await clientService.updateClient(client.id, { notes });
      toast.success('Notes saved');
      onUpdated(updated);
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
          <StickyNote size={11} /> Internal Notes
        </span>
        {canManage && isDirty && (
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : <><Check size={11} /> Save</>}
          </button>
        )}
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={8}
        disabled={!canManage}
        placeholder={canManage ? 'Add internal notes about this client…' : 'No notes.'}
        className={`${fieldCls} resize-none`}
      />
    </div>
  );
};

// ─── ClientDetailModal ───────────────────────────────────────────────────────

const TABS = ['Profile', 'Projects', 'Notes'];

const ClientDetailModal = ({ client, canManage, onClose, onUpdated, onDeleted }) => {
  const [tab, setTab] = useState('Profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    organization:  client.organization  || '',
    industry:      client.industry      || '',
    contact_phone: client.contact_phone || '',
    address:       client.address       || '',
    status:        client.status        || 'active',
  });
  const [saving, setSaving]     = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Projects tab
  const [allProjects, setAllProjects]         = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [savingProjects, setSavingProjects]   = useState(false);
  const [selectedIds, setSelectedIds]         = useState((client.project_ids || []).slice());
  const [projectSearch, setProjectSearch]     = useState('');

  useEffect(() => {
    if (tab !== 'Projects') return;
    setLoadingProjects(true);
    projectService.getProjects()
      .then(res => setAllProjects(Array.isArray(res) ? res : (res?.projects || [])))
      .catch(() => toast.error('Could not load projects'))
      .finally(() => setLoadingProjects(false));
  }, [tab]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const updated = await clientService.updateClient(client.id, form);
      toast.success('Client updated');
      onUpdated(updated);
      setEditing(false);
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProjects = async () => {
    setSavingProjects(true);
    try {
      const updated = await clientService.replaceClientProjects(client.user_id, selectedIds);
      toast.success('Projects updated');
      onUpdated(updated);
    } catch {
      toast.error('Failed to update projects');
    } finally {
      setSavingProjects(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await clientService.deleteClient(client.id);
      toast.success('Client deleted');
      onDeleted(client.id);
      onClose();
    } catch {
      toast.error('Delete failed');
      setDeleting(false);
    }
  };

  const toggleProject = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

  const linkedProjects    = allProjects.filter(p => selectedIds.includes(p.id));
  const unlinkdedFiltered = allProjects
    .filter(p => !selectedIds.includes(p.id))
    .filter(p => p.name?.toLowerCase().includes(projectSearch.toLowerCase()));

  return (
    <>
      <ModalOverlay isOpen onClose={onClose} closeOnOverlayClick zIndex={50}>
        <div className="flex h-[90vh] w-full max-w-2xl mx-4 flex-col rounded-lg overflow-hidden"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>

          {/* Header */}
          <div className="flex-shrink-0 px-5 py-4" style={{ backgroundColor: BRAND_COLORS.primary }}>
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-bold text-white rounded-xl"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                {getInitials(client.organization || client.name)}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-white truncate">
                  {client.organization || client.name || '—'}
                </h2>
                <p className="text-xs truncate text-white/70">{client.name} · {client.email}</p>
              </div>
              <div className="flex items-center flex-shrink-0 gap-2">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusCls(client.status)}`}>
                  {statusLabel(client.status)}
                </span>
                <button onClick={onClose}
                  className="flex items-center justify-center rounded-lg w-7 h-7 bg-white/10 hover:bg-white/20">
                  <X size={14} className="text-white" />
                </button>
              </div>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 mt-3">
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    tab === t ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white/80 hover:bg-white/10'
                  }`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">

            {/* ── Profile ── */}
            {tab === 'Profile' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                    Organization Details
                  </span>
                  {canManage && !editing && (
                    <button onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800">
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Organization</label>
                      <input value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} className={fieldCls} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Industry</label>
                        <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} className={fieldCls}>
                          <option value="">—</option>
                          {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Status</label>
                        <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={fieldCls}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Phone</label>
                      <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className={fieldCls} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Address</label>
                      <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className={fieldCls} />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setEditing(false)} className="px-3 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">Cancel</button>
                      <button onClick={handleSaveProfile} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                        style={{ backgroundColor: BRAND_COLORS.primary }}>
                        {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><Check size={12} /> Save</>}
                      </button>
                    </div>
                  </div>
                ) : (
                  <dl className="space-y-3">
                    {[
                      { icon: Building2, label: 'Organization', value: client.organization },
                      { icon: Globe2,    label: 'Industry',     value: client.industry },
                      { icon: Phone,     label: 'Phone',        value: client.contact_phone },
                      { icon: MapPin,    label: 'Address',      value: client.address },
                    ].map(({ icon: Icon, label, value }) => value ? (
                      <div key={label} className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
                          <Icon size={13} style={{ color: BRAND_COLORS.primary }} />
                        </span>
                        <div>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                          <p className="text-sm text-gray-800">{value}</p>
                        </div>
                      </div>
                    ) : null)}
                    <div className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
                        <Briefcase size={13} style={{ color: BRAND_COLORS.primary }} />
                      </span>
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Member Since</p>
                        <p className="text-sm text-gray-800">{fmt(client.created_at)}</p>
                      </div>
                    </div>
                  </dl>
                )}
              </div>
            )}

            {/* ── Projects ── */}
            {tab === 'Projects' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                    Linked Projects ({selectedIds.length})
                  </span>
                  {canManage && (
                    <button onClick={handleSaveProjects} disabled={savingProjects}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-semibold text-white disabled:opacity-50"
                      style={{ backgroundColor: BRAND_COLORS.primary }}>
                      {savingProjects ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : <><Check size={11} /> Save</>}
                    </button>
                  )}
                </div>

                {linkedProjects.length > 0 && (
                  <ul className="space-y-2">
                    {linkedProjects.map(p => (
                      <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border"
                        style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                        <FolderOpen size={13} style={{ color: BRAND_COLORS.primary }} className="flex-shrink-0" />
                        <span className="flex-1 text-sm font-medium text-gray-800 truncate">{p.name}</span>
                        {canManage && (
                          <button onClick={() => toggleProject(p.id)} className="p-1 text-gray-400 rounded-lg hover:bg-red-50 hover:text-red-500">
                            <X size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {canManage && (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2 border rounded-xl"
                      style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                      <Search size={13} className="flex-shrink-0 text-gray-400" />
                      <input value={projectSearch} onChange={e => setProjectSearch(e.target.value)}
                        placeholder="Search projects to link…"
                        className="flex-1 text-sm bg-transparent outline-none" />
                    </div>
                    {loadingProjects ? (
                      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
                        <Loader2 size={13} className="animate-spin" /> Loading projects…
                      </div>
                    ) : (
                      <ul className="space-y-1.5 max-h-52 overflow-y-auto">
                        {unlinkdedFiltered.map(p => (
                          <li key={p.id} onClick={() => toggleProject(p.id)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer hover:border-[#37559e]/30 hover:bg-blue-50/40 transition-colors"
                            style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                            <Plus size={12} className="flex-shrink-0 text-gray-400" />
                            <span className="flex-1 text-sm text-gray-700 truncate">{p.name}</span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${statusCls(p.status)}`}>
                              {p.status}
                            </span>
                          </li>
                        ))}
                        {unlinkdedFiltered.length === 0 && (
                          <li className="py-4 text-xs text-center text-gray-400">No more projects to link</li>
                        )}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Notes ── */}
            {tab === 'Notes' && (
              <NotesTab client={client} canManage={canManage} onUpdated={onUpdated} />
            )}
          </div>

          {/* Footer */}
          {canManage && (
            <div className="flex items-center justify-between flex-shrink-0 px-5 py-3 border-t"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
              <button onClick={() => setShowDelete(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-red-500 hover:bg-red-50 transition-colors">
                <Trash2 size={13} /> Delete client
              </button>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">
                Close
              </button>
            </div>
          )}
        </div>
      </ModalOverlay>

      {showDelete && (
        <DeleteConfirmModal
          client={client}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
          deleting={deleting}
        />
      )}
    </>
  );
};

export default ClientDetailModal;
